import React, { useState } from 'react';
import { supabase } from '../../../services/supabaseClient';

const ClientOverview = ({ client, contacts, addresses, consents, submissions, onRefresh }) => {
    const [editingContact, setEditingContact] = useState(null);
    const [contactDraft, setContactDraft] = useState(null);
    const [editingAddress, setEditingAddress] = useState(null);
    const [addressDraft, setAddressDraft] = useState(null);
    const [editingConsent, setEditingConsent] = useState(null);
    const [consentDraft, setConsentDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    // New item states
    const [showNewContact, setShowNewContact] = useState(false);
    const [newContact, setNewContact] = useState({ type: 'email', value: '', primary_flag: false });
    const [showNewAddress, setShowNewAddress] = useState(false);
    const [newAddress, setNewAddress] = useState({ type: 'home', line1: '', city: '', state: '', postal_code: '', country: '' });
    const [showNewConsent, setShowNewConsent] = useState(false);
    const [newConsent, setNewConsent] = useState({ consent_key: '', granted: true });

    const wrap = async (fn) => {
        setSaving(true);
        try {
            await fn();
            if (onRefresh) await onRefresh();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Quick Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Contacts */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                    <div className="bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between">
                        <h3 className="text-sm font-bold text-white uppercase tracking-wider">📞 Contacts</h3>
                        <button
                            onClick={() => setShowNewContact(!showNewContact)}
                            className="text-xs px-2 py-1 rounded bg-blue-600 text-white border border-blue-500 hover:bg-blue-700"
                        >
                            + Add
                        </button>
                    </div>
                    <div className="p-4">
                        {showNewContact && (
                            <div className="mb-3 p-3 bg-gray-700/50 rounded border border-blue-500">
                                <div className="space-y-2">
                                    <select
                                        className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                        value={newContact.type}
                                        onChange={(e) => setNewContact({ ...newContact, type: e.target.value })}
                                    >
                                        <option value="email">Email</option>
                                        <option value="phone">Phone</option>
                                        <option value="mobile">Mobile</option>
                                        <option value="whatsapp">WhatsApp</option>
                                    </select>
                                    <input
                                        className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                        placeholder="Value"
                                        value={newContact.value}
                                        onChange={(e) => setNewContact({ ...newContact, value: e.target.value })}
                                    />
                                    <label className="flex items-center gap-2 text-xs text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={newContact.primary_flag}
                                            onChange={(e) => setNewContact({ ...newContact, primary_flag: e.target.checked })}
                                            className="rounded border-gray-600 bg-gray-900"
                                        />
                                        Primary contact
                                    </label>
                                    <div className="flex gap-2">
                                        <button
                                            disabled={saving || !newContact.value.trim()}
                                            onClick={() => wrap(async () => {
                                                await supabase.from('client_contacts').insert([{
                                                    client_id: client.id,
                                                    type: newContact.type,
                                                    value: newContact.value,
                                                    primary_flag: newContact.primary_flag
                                                }]);
                                                setNewContact({ type: 'email', value: '', primary_flag: false });
                                                setShowNewContact(false);
                                            })}
                                            className="text-xs px-2 py-1 rounded bg-green-600 text-white border border-green-500"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowNewContact(false);
                                                setNewContact({ type: 'email', value: '', primary_flag: false });
                                            }}
                                            className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-200 border border-gray-500"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        <ul className="space-y-3">
                            {contacts.map((ct) => {
                                const isEditing = editingContact === ct.id;
                                const draft = isEditing ? contactDraft : ct;
                                return (
                                    <li key={ct.id} className="p-3 bg-gray-700/50 rounded border border-gray-600">
                                        {isEditing ? (
                                            <div className="space-y-2">
                                                <select
                                                    className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                    value={draft.type}
                                                    onChange={(e) => setContactDraft({ ...draft, type: e.target.value })}
                                                >
                                                    <option value="email">Email</option>
                                                    <option value="phone">Phone</option>
                                                    <option value="mobile">Mobile</option>
                                                    <option value="whatsapp">WhatsApp</option>
                                                </select>
                                                <input
                                                    className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                    value={draft.value}
                                                    onChange={(e) => setContactDraft({ ...draft, value: e.target.value })}
                                                />
                                                <label className="flex items-center gap-2 text-xs text-gray-300">
                                                    <input
                                                        type="checkbox"
                                                        checked={draft.primary_flag}
                                                        onChange={(e) => setContactDraft({ ...draft, primary_flag: e.target.checked })}
                                                        className="rounded border-gray-600 bg-gray-900"
                                                    />
                                                    Primary contact
                                                </label>
                                                <div className="flex gap-2">
                                                    <button
                                                        disabled={saving}
                                                        onClick={() => wrap(async () => {
                                                            await supabase.from('client_contacts').update({
                                                                type: draft.type,
                                                                value: draft.value,
                                                                primary_flag: draft.primary_flag
                                                            }).eq('id', ct.id);
                                                            setEditingContact(null);
                                                            setContactDraft(null);
                                                        })}
                                                        className="text-xs px-2 py-1 rounded bg-green-600 text-white border border-green-500"
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={() => { setEditingContact(null); setContactDraft(null); }}
                                                        className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-200 border border-gray-500"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-xs font-bold text-gray-400 uppercase">{ct.type}</span>
                                                    <div className="flex items-center gap-2">
                                                        {ct.primary_flag && <span className="px-2 py-0.5 rounded-full bg-green-900/30 text-green-300 border border-green-800 text-xs">✓ Primary</span>}
                                                        <button
                                                            onClick={() => { setEditingContact(ct.id); setContactDraft({ ...ct }); }}
                                                            className="text-xs px-2 py-1 rounded bg-blue-700 text-white border border-blue-500"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                if (!window.confirm('Delete this contact?')) return;
                                                                wrap(async () => {
                                                                    await supabase.from('client_contacts').delete().eq('id', ct.id);
                                                                });
                                                            }}
                                                            className="text-xs px-2 py-1 rounded bg-red-700 text-white border border-red-500"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="text-sm text-white font-mono">{ct.value}</div>
                                            </>
                                        )}
                                    </li>
                                );
                            })}
                            {contacts.length === 0 && (
                                <li className="text-sm text-gray-400 text-center py-8">No contacts available</li>
                            )}
                        </ul>
                    </div>
                </div>

                {/* Addresses */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                    <div className="bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between">
                        <h3 className="text-sm font-bold text-white uppercase tracking-wider">📍 Addresses</h3>
                        <button
                            onClick={() => setShowNewAddress(!showNewAddress)}
                            className="text-xs px-2 py-1 rounded bg-blue-600 text-white border border-blue-500 hover:bg-blue-700"
                        >
                            + Add
                        </button>
                    </div>
                    <div className="p-4">
                        {showNewAddress && (
                            <div className="mb-3 p-3 bg-gray-700/50 rounded border border-blue-500">
                                <div className="space-y-2">
                                    <select
                                        className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                        value={newAddress.type}
                                        onChange={(e) => setNewAddress({ ...newAddress, type: e.target.value })}
                                    >
                                        <option value="home">Home</option>
                                        <option value="work">Work</option>
                                        <option value="mailing">Mailing</option>
                                    </select>
                                    <input
                                        className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                        placeholder="Address line 1"
                                        value={newAddress.line1}
                                        onChange={(e) => setNewAddress({ ...newAddress, line1: e.target.value })}
                                    />
                                    <input
                                        className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                        placeholder="City"
                                        value={newAddress.city}
                                        onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        <input
                                            className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                            placeholder="State"
                                            value={newAddress.state}
                                            onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                                        />
                                        <input
                                            className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                            placeholder="Postal code"
                                            value={newAddress.postal_code}
                                            onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                                        />
                                    </div>
                                    <input
                                        className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                        placeholder="Country"
                                        value={newAddress.country}
                                        onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })}
                                    />
                                    <div className="flex gap-2">
                                        <button
                                            disabled={saving || !newAddress.line1.trim()}
                                            onClick={() => wrap(async () => {
                                                await supabase.from('client_addresses').insert([{
                                                    client_id: client.id,
                                                    type: newAddress.type,
                                                    line1: newAddress.line1,
                                                    city: newAddress.city,
                                                    state: newAddress.state,
                                                    postal_code: newAddress.postal_code,
                                                    country: newAddress.country
                                                }]);
                                                setNewAddress({ type: 'home', line1: '', city: '', state: '', postal_code: '', country: '' });
                                                setShowNewAddress(false);
                                            })}
                                            className="text-xs px-2 py-1 rounded bg-green-600 text-white border border-green-500"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowNewAddress(false);
                                                setNewAddress({ type: 'home', line1: '', city: '', state: '', postal_code: '', country: '' });
                                            }}
                                            className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-200 border border-gray-500"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        <ul className="space-y-3">
                            {addresses.map((ad) => {
                                const isEditing = editingAddress === ad.id;
                                const draft = isEditing ? addressDraft : ad;
                                return (
                                    <li key={ad.id} className="p-3 bg-gray-700/50 rounded border border-gray-600">
                                        {isEditing ? (
                                            <div className="space-y-2">
                                                <select
                                                    className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                    value={draft.type}
                                                    onChange={(e) => setAddressDraft({ ...draft, type: e.target.value })}
                                                >
                                                    <option value="home">Home</option>
                                                    <option value="work">Work</option>
                                                    <option value="mailing">Mailing</option>
                                                </select>
                                                <input
                                                    className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                    placeholder="Address line 1"
                                                    value={draft.line1 || ''}
                                                    onChange={(e) => setAddressDraft({ ...draft, line1: e.target.value })}
                                                />
                                                <input
                                                    className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                    placeholder="City"
                                                    value={draft.city || ''}
                                                    onChange={(e) => setAddressDraft({ ...draft, city: e.target.value })}
                                                />
                                                <div className="grid grid-cols-2 gap-2">
                                                    <input
                                                        className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                        placeholder="State"
                                                        value={draft.state || ''}
                                                        onChange={(e) => setAddressDraft({ ...draft, state: e.target.value })}
                                                    />
                                                    <input
                                                        className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                        placeholder="Postal code"
                                                        value={draft.postal_code || ''}
                                                        onChange={(e) => setAddressDraft({ ...draft, postal_code: e.target.value })}
                                                    />
                                                </div>
                                                <input
                                                    className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                    placeholder="Country"
                                                    value={draft.country || ''}
                                                    onChange={(e) => setAddressDraft({ ...draft, country: e.target.value })}
                                                />
                                                <div className="flex gap-2">
                                                    <button
                                                        disabled={saving}
                                                        onClick={() => wrap(async () => {
                                                            await supabase.from('client_addresses').update({
                                                                type: draft.type,
                                                                line1: draft.line1,
                                                                city: draft.city,
                                                                state: draft.state,
                                                                postal_code: draft.postal_code,
                                                                country: draft.country
                                                            }).eq('id', ad.id);
                                                            setEditingAddress(null);
                                                            setAddressDraft(null);
                                                        })}
                                                        className="text-xs px-2 py-1 rounded bg-green-600 text-white border border-green-500"
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={() => { setEditingAddress(null); setAddressDraft(null); }}
                                                        className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-200 border border-gray-500"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="text-xs font-bold text-gray-400 uppercase">{ad.type}</div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => { setEditingAddress(ad.id); setAddressDraft({ ...ad }); }}
                                                            className="text-xs px-2 py-1 rounded bg-blue-700 text-white border border-blue-500"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                if (!window.confirm('Delete this address?')) return;
                                                                wrap(async () => {
                                                                    await supabase.from('client_addresses').delete().eq('id', ad.id);
                                                                });
                                                            }}
                                                            className="text-xs px-2 py-1 rounded bg-red-700 text-white border border-red-500"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="text-sm text-white space-y-0.5">
                                                    {ad.line1 && <div>{ad.line1}</div>}
                                                    {ad.city && <div>{ad.city}</div>}
                                                    {ad.country && <div>{ad.country}</div>}
                                                </div>
                                            </>
                                        )}
                                    </li>
                                );
                            })}
                            {addresses.length === 0 && (
                                <li className="text-sm text-gray-400 text-center py-8">No addresses available</li>
                            )}
                        </ul>
                    </div>
                </div>

                {/* Consents */}
                <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                    <div className="bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between">
                        <h3 className="text-sm font-bold text-white uppercase tracking-wider">✅ Consents</h3>
                        <button
                            onClick={() => setShowNewConsent(!showNewConsent)}
                            className="text-xs px-2 py-1 rounded bg-blue-600 text-white border border-blue-500 hover:bg-blue-700"
                        >
                            + Add
                        </button>
                    </div>
                    <div className="p-4">
                        {showNewConsent && (
                            <div className="mb-3 p-3 bg-gray-700/50 rounded border border-blue-500">
                                <div className="space-y-2">
                                    <input
                                        className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                        placeholder="Consent key (e.g., marketing_emails)"
                                        value={newConsent.consent_key}
                                        onChange={(e) => setNewConsent({ ...newConsent, consent_key: e.target.value })}
                                    />
                                    <label className="flex items-center gap-2 text-xs text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={newConsent.granted}
                                            onChange={(e) => setNewConsent({ ...newConsent, granted: e.target.checked })}
                                            className="rounded border-gray-600 bg-gray-900"
                                        />
                                        Granted
                                    </label>
                                    <div className="flex gap-2">
                                        <button
                                            disabled={saving || !newConsent.consent_key.trim()}
                                            onClick={() => wrap(async () => {
                                                await supabase.from('client_consents').insert([{
                                                    client_id: client.id,
                                                    consent_key: newConsent.consent_key,
                                                    granted: newConsent.granted,
                                                    granted_at: newConsent.granted ? new Date().toISOString() : null
                                                }]);
                                                setNewConsent({ consent_key: '', granted: true });
                                                setShowNewConsent(false);
                                            })}
                                            className="text-xs px-2 py-1 rounded bg-green-600 text-white border border-green-500"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowNewConsent(false);
                                                setNewConsent({ consent_key: '', granted: true });
                                            }}
                                            className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-200 border border-gray-500"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        <ul className="space-y-3">
                            {consents.map((cn) => {
                                const isEditing = editingConsent === cn.id;
                                const draft = isEditing ? consentDraft : cn;
                                return (
                                    <li key={cn.id} className="p-3 bg-gray-700/50 rounded border border-gray-600">
                                        {isEditing ? (
                                            <div className="space-y-2">
                                                <input
                                                    className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                    value={draft.consent_key}
                                                    onChange={(e) => setConsentDraft({ ...draft, consent_key: e.target.value })}
                                                />
                                                <label className="flex items-center gap-2 text-xs text-gray-300">
                                                    <input
                                                        type="checkbox"
                                                        checked={draft.granted}
                                                        onChange={(e) => setConsentDraft({ ...draft, granted: e.target.checked })}
                                                        className="rounded border-gray-600 bg-gray-900"
                                                    />
                                                    Granted
                                                </label>
                                                <div className="flex gap-2">
                                                    <button
                                                        disabled={saving}
                                                        onClick={() => wrap(async () => {
                                                            await supabase.from('client_consents').update({
                                                                consent_key: draft.consent_key,
                                                                granted: draft.granted,
                                                                granted_at: draft.granted ? (draft.granted_at || new Date().toISOString()) : null
                                                            }).eq('id', cn.id);
                                                            setEditingConsent(null);
                                                            setConsentDraft(null);
                                                        })}
                                                        className="text-xs px-2 py-1 rounded bg-green-600 text-white border border-green-500"
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={() => { setEditingConsent(null); setConsentDraft(null); }}
                                                        className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-200 border border-gray-500"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-xs font-bold text-white">{cn.consent_key}</span>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-0.5 rounded-full text-xs ${cn.granted ? 'bg-green-900/30 text-green-300 border border-green-800' : 'bg-red-900/30 text-red-300 border border-red-800'}`}>
                                                            {cn.granted ? '✓ Granted' : '✗ Denied'}
                                                        </span>
                                                        <button
                                                            onClick={() => { setEditingConsent(cn.id); setConsentDraft({ ...cn }); }}
                                                            className="text-xs px-2 py-1 rounded bg-blue-700 text-white border border-blue-500"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                if (!window.confirm('Delete this consent?')) return;
                                                                wrap(async () => {
                                                                    await supabase.from('client_consents').delete().eq('id', cn.id);
                                                                });
                                                            }}
                                                            className="text-xs px-2 py-1 rounded bg-red-700 text-white border border-red-500"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="text-xs text-gray-400">{cn.granted_at || '-'}</div>
                                            </>
                                        )}
                                    </li>
                                );
                            })}
                            {consents.length === 0 && (
                                <li className="text-sm text-gray-400 text-center py-8">No consents available</li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Wizard Submissions */}
            {submissions && submissions.length > 0 && (
                <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                    <div className="bg-gray-900/50 p-4 border-b border-gray-700">
                        <h3 className="text-sm font-bold text-white uppercase tracking-wider">🧙 Wizard Submissions</h3>
                    </div>
                    <div className="p-4">
                        <div className="space-y-3">
                            {submissions.map((sub) => (
                                <div key={sub.id} className="p-4 bg-gray-700/50 rounded border border-gray-600">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-bold text-white">Submission #{sub.id.slice(0, 8)}</span>
                                        <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${sub.status === 'completed' ? 'bg-green-900/30 text-green-300 border border-green-800' : 'bg-yellow-900/30 text-yellow-300 border border-yellow-800'}`}>
                                            {sub.status || 'new'}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                                        <div>Created: {new Date(sub.created_at).toLocaleDateString()}</div>
                                        <div>Pathway: {sub.selected_pathway || '-'}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientOverview;
